import { QueryClient } from "react-query";
import { clearLaunchCreds } from "./launchCreds";

export const sessionKey = "iconik_session";

/**
 * Handles calls to the api server
 * Automatically adds session id to requests
 */
export default class ApiService {
  static apiUrl = process.env.REACT_APP_API_URL;
  static queryClient = new QueryClient({
    // defaultOptions: { queries: { staleTime: 5000 } },
  });

  /**
   * Make a request to the api server
   * @param {string} method HTTP method
   * @param {string} path HTTP path
   * @param {object} body HTTP body
   * @returns Response data
   */
  public static async fetch<T, B = unknown>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: B
  ): Promise<T> {
    const request = {
      body: method !== "GET" ? JSON.stringify(body) : undefined,
      method,
      headers: this.getHeaders(),
    };

    const response = await fetch(this.apiUrl + path, request);

    return this.handleResponse(response);
  }

  public static getSession() {
    const session = localStorage.getItem(sessionKey);
    if (session) {
      const sessionJson = JSON.parse(session);
      if (sessionJson.expires >= Date.now() / 1000) {
        return sessionJson as SessionInfo;
      } else {
        // Session expired — clear both the session and any saved launch creds so
        // they don't outlive the session that protected them.
        localStorage.removeItem(sessionKey);
        clearLaunchCreds();
        this.queryClient.invalidateQueries(["session"]);
      }
    }
    return undefined;
  }

  private static getHeaders() {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const session = this.getSession();

    headers.Authorization = `Bearer ${session?.id}`;

    return headers;
  }

  private static async handleResponse(response: Response) {
    const data = await response.json();

    if (response.ok) {
      return data;
    } else if (response.status === 401) {
      // Session is no longer valid — clear both the manager session and any saved
      // launch creds so they don't persist past the end of this auth session.
      clearLaunchCreds();
      this.queryClient.invalidateQueries(["session"]);
    }

    const error = new Error(data.error?.message);
    error.name = data.error?.name || "Error";
    throw error;
  }
}
